import LinkedInOpsPage from './views/LinkedInOps/LinkedInOpsView';
import { CallProvider } from './contexts/CallContext';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, ToastBar } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import Dashboard from './pages/Dashboard';
import Requests from './pages/Requests';
import Brands from './pages/Brands';
import Chat from './pages/Chat';
import Team from './pages/Team';
import Kanban from './pages/Kanban';
import Calendar from './pages/Calendar';
import MetaCallback from './pages/MetaCallback';
import Drive from './pages/Drive';
import Content from './pages/Content';
import Production from './pages/Production';
import Processes from './pages/Processes';

const RoleGuard: React.FC<{children: React.ReactNode; allowed: string[]; fallback: string}> = ({children, allowed, fallback}) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user && !allowed.includes(user.role)) return <Navigate to={fallback} replace/>;
  return <>{children}</>;
};
const PrivateRoute: React.FC<{children: React.ReactNode}> = ({children}) => {
  const { token, loading } = useAuth();
  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#060d1a',fontFamily:'"Montserrat",sans-serif',flexDirection:'column',gap:'14px'}}>
      <div style={{fontSize:'22px',fontWeight:900,color:'white'}}>ZOIK<span style={{color:'#2688c9'}}>|</span>Analyst</div>
      <div style={{fontSize:'12px',color:'rgba(255,255,255,0.3)'}}>Cargando...</div>
    </div>
  );
  return token ? <>{children}</> : <Navigate to="/login" replace/>;
};

const AdminRoute: React.FC<{children: React.ReactNode}> = ({children}) => {
  const { user, token, loading } = useAuth();
  if (loading) return null;
  if (!token) return <Navigate to="/login" replace/>;
  if (!user || !['ADMIN','CUENTAS'].includes(user.role)) return <Navigate to="/dashboard" replace/>;
  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <CallProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3800,
            style: {
              background: 'rgba(255, 255, 255, 0.86)',
              backdropFilter: 'blur(30px) saturate(190%)',
              WebkitBackdropFilter: 'blur(30px) saturate(190%)',
              border: '1px solid rgba(255, 255, 255, 0.95)',
              borderRadius: '20px',
              padding: '12px 16px',
              color: '#0f2a4a',
              boxShadow: '0 16px 36px -6px rgba(15, 42, 74, 0.16), 0 4px 14px rgba(15, 42, 74, 0.06), inset 0 1px 1.5px rgba(255, 255, 255, 0.98)',
              fontFamily: '"Montserrat", -apple-system, sans-serif',
              fontSize: '13px',
              fontWeight: 600,
              maxWidth: '420px',
            },
            success: {
              iconTheme: {
                primary: '#34c759',
                secondary: '#ffffff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ff3b30',
                secondary: '#ffffff',
              },
            },
          }}
        >
          {(t) => (
            <ToastBar
              toast={t}
              style={{
                ...t.style,
                animation: t.visible
                  ? 'appleToastEnter 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                  : 'appleToastExit 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards',
              }}
            >
              {({ icon, message }) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '100%', minWidth: '220px' }}>
                  {/* Apple Notification Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '5px',
                        background: 'linear-gradient(135deg, #0070f3, #0f2a4a)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '9px',
                        fontWeight: 900,
                        boxShadow: '0 2px 4px rgba(0, 112, 243, 0.3)'
                      }}>
                        Z
                      </div>
                      <span style={{ fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px', color: 'rgba(15, 42, 74, 0.5)', textTransform: 'uppercase' }}>
                        Zoik Analyst
                      </span>
                    </div>
                    <span style={{ fontSize: '9.5px', fontWeight: 600, color: 'rgba(15, 42, 74, 0.38)' }}>
                      ahora
                    </span>
                  </div>
                  {/* Apple Notification Content */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '2px' }}>
                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                      {icon}
                    </div>
                    <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#0f2a4a', lineHeight: '1.4', flex: 1 }}>
                      {message}
                    </div>
                  </div>
                </div>
              )}
            </ToastBar>
          )}
        </Toaster>
                <Routes>
          {/* Rutas Principales Zoik Analyst */}
          <Route path="/" element={<Navigate to="/dashboard" replace/>}/>
          <Route path="/login" element={<Login/>}/>
          <Route path="/invite/:token" element={<AcceptInvite/>}/>
          <Route path="/dashboard" element={<PrivateRoute><Dashboard/></PrivateRoute>}/>
        <Route path="/linkedin" element={<PrivateRoute><LinkedInOpsPage/></PrivateRoute>}/>
          <Route path="/requests" element={<PrivateRoute><Requests/></PrivateRoute>}/>
          <Route path="/brands" element={<PrivateRoute><Brands/></PrivateRoute>}/>
          <Route path="/chat" element={<PrivateRoute><Chat/></PrivateRoute>}/>
          <Route path="/kanban" element={<PrivateRoute><Kanban/></PrivateRoute>}/>
          <Route path="/drive" element={<PrivateRoute><Drive/></PrivateRoute>}/>
          <Route path="/meta-callback" element={<MetaCallback/>}/>
          <Route path="/calendar" element={<PrivateRoute><Calendar/></PrivateRoute>}/>
          <Route path="/production" element={<PrivateRoute><RoleGuard allowed={['ADMIN']} fallback="/dashboard"><Production/></RoleGuard></PrivateRoute>}/>
          <Route path="/content" element={<PrivateRoute><Content/></PrivateRoute>}/>
          <Route path="/processes" element={<PrivateRoute><Processes/></PrivateRoute>}/>
          <Route path="/team" element={<PrivateRoute><Team/></PrivateRoute>}/>

          {/* Redirecciones de retrocompatibilidad */}
          <Route path="/platform/login" element={<Navigate to="/login" replace/>}/>
          <Route path="/platform/dashboard" element={<Navigate to="/dashboard" replace/>}/>
          <Route path="/platform/invite/:token" element={<AcceptInvite/>}/>
          <Route path="/platform/*" element={<Navigate to="/dashboard" replace/>}/>

          <Route path="*" element={<Navigate to="/dashboard" replace/>}/>
        </Routes>
      </BrowserRouter>
      </CallProvider>
    </AuthProvider>
  );
}

export default App;
